/**
 * Layered left-to-right layout for the pipeline DAG.
 *
 * Pure function: given the graph nodes (each carrying a 0-based `wave` from
 * the backend topological sort), assign an (x, y) position so each wave is a
 * column and nodes within a wave stack vertically. Keeps React Flow purely
 * presentational — no client-side topo sort, no dagre dependency.
 */

import type { IPipelineGraphNode } from "../components/PipelineGraph/interfaces"

/** Horizontal gap between successive waves (columns), in px. */
const COLUMN_GAP = 260
/** Vertical gap between nodes within the same wave, in px. */
const ROW_GAP = 90

export interface IPositionedNode {
  id: string
  x: number
  y: number
}

/**
 * Position every node on a wave-based grid. Nodes are grouped by `wave`
 * (column) and centred vertically within their column so a sparse wave sits
 * in the middle rather than top-aligned. Deterministic: input order within a
 * wave is preserved.
 */
export function layoutPipelineNodes(nodes: IPipelineGraphNode[]): IPositionedNode[] {
  const byWave = new Map<number, IPipelineGraphNode[]>()
  for (const node of nodes) {
    const bucket = byWave.get(node.wave)
    if (bucket) {
      bucket.push(node)
    } else {
      byWave.set(node.wave, [node])
    }
  }

  let tallest = 0
  for (const bucket of byWave.values()) {
    tallest = Math.max(tallest, bucket.length)
  }
  const canvasHeight = Math.max(tallest, 1) * ROW_GAP

  const positioned: IPositionedNode[] = []
  for (const [wave, bucket] of byWave) {
    const columnHeight = bucket.length * ROW_GAP
    const top = (canvasHeight - columnHeight) / 2
    bucket.forEach((node, index) => {
      positioned.push({
        id: node.id,
        x: wave * COLUMN_GAP,
        y: top + index * ROW_GAP,
      })
    })
  }

  return positioned
}
